import { decode } from 'base64-arraybuffer';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

const KEY_CURRENT_USER = 'current-user';

let SDKRuntime;
function setSDKRuntime(runtime) {
    SDKRuntime = runtime;
}

class Qiniu {
    upload(name, data, tokens, options) {
        return SDKRuntime.http.upload({
            method: 'POST',
            url: tokens.upload_url,
            file: { name, data, field: 'file' },
            form: { name, key: tokens.key, token: tokens.token },
            header: options === null || options === void 0 ? void 0 : options.header,
        }, options);
    }
}

const providers = {
    qiniu: new Qiniu(),
};

class LCFile {
    constructor(_object) {
        this._object = _object;
    }
    get app() {
        return this._object.app;
    }
    get className() {
        return '_File';
    }
    get id() {
        return this._object.id;
    }
    get data() {
        return this._object.data;
    }
    get name() {
        return this.data.name;
    }
    get url() {
        return this.data.url;
    }
    get size() {
        var _a;
        return (_a = this.data.metaData) === null || _a === void 0 ? void 0 : _a.size;
    }
    get mime() {
        return this.data.mime_type;
    }
    static fromJSON(app, data) {
        return new LCFile(app.database().decodeObject(data, '_File'));
    }
    thumbnailURL(width, height, quality = 100, scaleToFit = true, format = 'png') {
        const mode = scaleToFit ? 2 : 1;
        return this.url + `?imageView/${mode}/w/${width}/h/${height}/q/${quality}/format/${format}`;
    }
    delete(options) {
        return this.app.database().class(this.className).object(this.id).delete(options);
    }
    toJSON() {
        return this._object.toJSON();
    }
    _LC_encode(options) {
        const encoded = this._object._LC_encode(options);
        if (encoded.__type === 'Object') {
            encoded.__type = 'File';
        }
        return encoded;
    }
}

class Storage {
    constructor(app) {
        this.app = app;
    }
    queryFile() {
        return this.app.database().class('_File', LCFile.fromJSON);
    }
    upload(name, data, options) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            data = parseFileData(data);
            const metaData = Object.assign({}, options === null || options === void 0 ? void 0 : options.metaData);
            metaData.owner = (yield getCurrentUserID(this.app)) || 'unknown';
            if (typeof metaData.size !== 'number') {
                metaData.size = (_a = data.size) !== null && _a !== void 0 ? _a : data.length;
            }
            const tokens = yield this._getFileTokens(name, Object.assign(Object.assign({}, options), { metaData }));
            const provider = this._getFileProvider(tokens.provider);
            try {
                yield provider.upload(name, data, tokens, options);
                yield this._invokeFileCallback(tokens.token, true);
            }
            catch (e) {
                yield this._invokeFileCallback(tokens.token, false);
                throw e;
            }
            return LCFile.fromJSON(this.app, Object.assign(Object.assign({}, tokens), { name,
                metaData }));
        });
    }
    uploadWithURL(name, url, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const metaData = Object.assign(Object.assign({}, options === null || options === void 0 ? void 0 : options.metaData), { __source: 'external', owner: (yield getCurrentUserID(this.app)) || 'unknown', size: 0 });
            const obj = yield this.app
                .database()
                .class('_File')
                .add({
                name,
                url,
                metaData,
                ACL: options === null || options === void 0 ? void 0 : options.ACL,
                mime_type: options === null || options === void 0 ? void 0 : options.mime,
            }, Object.assign(Object.assign({}, options), { fetchData: true }));
            return new LCFile(obj);
        });
    }
    _getFileTokens(name, options) {
        return this.app.request({
            method: 'POST',
            path: '/1.1/fileTokens',
            body: {
                name,
                ACL: options === null || options === void 0 ? void 0 : options.ACL,
                key: options === null || options === void 0 ? void 0 : options.key,
                metaData: options === null || options === void 0 ? void 0 : options.metaData,
                mime_type: options === null || options === void 0 ? void 0 : options.mime,
            },
        });
    }
    _getFileProvider(name) {
        if (name in providers) {
            return providers[name];
        }
        throw new Error(`暂不支持上传文件到 ${name}`);
    }
    _invokeFileCallback(token, success) {
        return this.app.request({
            method: 'POST',
            path: '/1.1/fileCallback',
            body: { token, result: success },
        });
    }
}
function getCurrentUserID(app) {
    return __awaiter(this, void 0, void 0, function* () {
        if (app.payload[KEY_CURRENT_USER]) {
            return app.payload[KEY_CURRENT_USER].id;
        }
        const user_str = yield app.localStorage.getAsync(KEY_CURRENT_USER);
        if (user_str) {
            return JSON.parse(user_str).objectId;
        }
        return null;
    });
}
function base64InDataURLs(urls) {
    if (urls.startsWith('data:')) {
        const [meta, data] = urls.split(',');
        if (meta === null || meta === void 0 ? void 0 : meta.endsWith('base64')) {
            return data;
        }
    }
    return urls;
}
function parseFileData(data) {
    if (typeof data === 'string') {
        if (typeof btoa !== 'undefined') {
            return parseFileData({ base64: btoa(data) });
        }
        if (typeof Buffer !== 'undefined') {
            return Buffer.from(data, 'utf-8');
        }
        throw new Error('当前平台不支持使用 utf-8 编码的字符串构建文件');
    }
    if (Array.isArray(data)) {
        const u8arr = new Uint8Array(data.length);
        data.forEach((v, i) => (u8arr[i] = v));
        if (typeof Blob !== 'undefined') {
            return new Blob([u8arr]);
        }
        if (typeof Buffer !== 'undefined') {
            return Buffer.from(u8arr);
        }
        throw new Error('当前平台不支持使用字节数组构建文件');
    }
    if (typeof data.base64 === 'string') {
        const base64 = base64InDataURLs(data.base64);
        if (typeof Blob !== 'undefined') {
            return new Blob([decode(base64)]);
        }
        if (typeof Buffer !== 'undefined') {
            return Buffer.from(base64, 'base64');
        }
        throw new Error('当前平台不支持使用 base64 编码的字符串构建文件');
    }
    if (data.blob) {
        return data.blob;
    }
    return data;
}

const storageModule = {
    name: 'storage',
    components: { providers, Storage, LCFile },
    onLoad: (runtime) => {
        setSDKRuntime(runtime);
        const { App } = runtime.modules.core.components;
        App.prototype.storage = function () {
            return new Storage(this);
        };
    },
};

export { Storage, providers, storageModule };
